// CAT Mock 4 — VARC section.
const catMock4Varc = [
  {
    id: 1,
    type: "mcq",
    passageId: "rc1",
    passage: `Advocates of "nudging" argue that because every choice is presented in some way — a cafeteria must place its food in some order, a form must have some default — the designer of a choice cannot avoid influencing it, and might as well influence it for the better. Put the fruit at eye level, make enrolment in a pension scheme automatic but allow people to opt out, and behaviour shifts without anyone being forbidden anything. The appeal is that the approach seems to sidestep the old quarrel between paternalists, who would restrict choices for people's own good, and libertarians, who would leave people to their own mistakes. Nobody's options are removed; only the path of least resistance is moved.

Critics have pressed two objections, of unequal force. The first is that nudges are manipulative: they work by exploiting quirks of cognition, such as inertia, rather than by giving people reasons. This objection is weaker than it sounds, since the alternative to a deliberately chosen default is not a neutral presentation but an accidentally chosen one, which exploits the same inertia to no one's benefit. The second objection is more troubling. A nudge is justified by the claim that it steers people towards what they themselves would choose on reflection. But the evidence that a default "works" is usually that many people stay with it — which is exactly what inertia would produce whether or not the default matched their considered preferences. The success of a nudge is thus poor evidence of its legitimacy. What would count as evidence is harder to obtain: some independent way of finding out what people would want if they thought about it, which is precisely the information whose absence made the nudge seem necessary.`,
    question: "The author considers the charge that nudges are manipulative to be \"weaker than it sounds\" because:",
    options: [
      "A. most people are aware that defaults are chosen deliberately and can resist them.",
      "B. every way of presenting a choice exploits the same tendencies, so declining to design a default leaves the influence to chance rather than removing it.",
      "C. inertia is not, in fact, a quirk of cognition but a rational response to complexity.",
      "D. nudges always give people reasons in addition to changing defaults."
    ],
    answer: "B"
  },
  {
    id: 2,
    type: "mcq",
    passageId: "rc1",
    question: "According to the passage, the second objection is \"more troubling\" chiefly because:",
    options: [
      "A. nudges remove options that people would otherwise have chosen.",
      "B. libertarians and paternalists both reject nudging.",
      "C. defaults can be changed too easily by those who design them.",
      "D. people staying with a default does not show that the default reflects what they would choose on reflection."
    ],
    answer: "D"
  },
  {
    id: 3,
    type: "mcq",
    passageId: "rc1",
    question: "The last sentence of the passage implies that:",
    options: [
      "A. the information needed to justify a nudge is the very information whose lack made the nudge seem necessary, so justification is hard to come by.",
      "B. nudges should be abandoned in favour of outright restrictions.",
      "C. surveys of people's preferences are always more reliable than observed behaviour.",
      "D. designers of choices already possess reliable knowledge of people's reflective preferences."
    ],
    answer: "A"
  },
  {
    id: 4,
    type: "mcq",
    passageId: "rc1",
    question: "Which of the following findings would most strengthen the second objection discussed in the passage?",
    options: [
      "A. Employees automatically enrolled in a pension scheme saved more on average than those who had to opt in.",
      "B. Cafeterias that placed fruit at eye level sold more fruit and less dessert.",
      "C. Most employees automatically enrolled at a 3% contribution rate stayed at 3%, although in detailed interviews most said they would prefer to save substantially more.",
      "D. Some employees opted out of automatic enrolment within the first month."
    ],
    answer: "C"
  },
  {
    id: 5,
    type: "mcq",
    passageId: "rc2",
    passage: `For much of the twentieth century, performers of early music aimed at what came to be called historical authenticity: to play Bach as Bach's own musicians would have played him, on instruments of the period, at the tempos and with the ornamentation that surviving treatises recommended. The movement transformed the sound of baroque music, stripping away the heavy orchestras and slow tempos that the nineteenth century had bequeathed. Yet its central claim was always open to an awkward question. Even if we could reproduce exactly the sounds an eighteenth-century audience heard, we could not reproduce the audience. Listeners whose ears have been formed by Beethoven, Wagner and the electric guitar do not hear a harpsichord as Bach's contemporaries did; to them it may sound quaint where it once sounded ordinary, or brilliant where it once sounded thin.

Some critics concluded that the whole enterprise was a modern style in period costume — that its brisk tempos and lean textures owed more to the aesthetics of the mid-twentieth century than to any recovered past. There is something to this, but it does not follow that the research was idle. Learning how a piece was first performed widens the range of choices available to a performer and exposes assumptions that had passed for timeless taste. The mistake lay not in the research but in the word: "authentic" promised a correctness that no performance can possess, when what the movement actually offered was a set of informed and arguable interpretations. The more modest label that many performers now prefer, "historically informed", concedes the point without abandoning the work.`,
    question: "The \"awkward question\" faced by the authenticity movement is that:",
    options: [
      "A. even an exact reproduction of the original sound cannot recreate the way the original audience perceived it.",
      "B. period instruments are too rare and fragile to be played regularly.",
      "C. surviving treatises disagree about the correct tempos for baroque music.",
      "D. modern audiences prefer the heavier sound of nineteenth-century orchestras."
    ],
    answer: "A"
  },
  {
    id: 6,
    type: "mcq",
    passageId: "rc2",
    question: "How does the author respond to the critics who called the movement \"a modern style in period costume\"?",
    options: [
      "A. The author rejects their claim as entirely unfounded.",
      "B. The author accepts their claim and concludes that the research was wasted.",
      "C. The author grants that their observation has some merit but denies that it makes the research worthless.",
      "D. The author argues that the critics themselves were influenced by nineteenth-century taste."
    ],
    answer: "C"
  },
  {
    id: 7,
    type: "mcq",
    passageId: "rc2",
    question: "According to the author, the movement's real mistake was that:",
    options: [
      "A. it relied on unreliable historical treatises.",
      "B. its chosen label promised a correctness that no performance can achieve.",
      "C. it ignored the music of Beethoven and Wagner.",
      "D. it adopted tempos that were too fast for modern listeners."
    ],
    answer: "B"
  },
  {
    id: 8,
    type: "mcq",
    passageId: "rc2",
    question: "Which of the following is most analogous to the change of label from \"authentic\" to \"historically informed\"?",
    options: [
      "A. A publisher reprinting a classic novel with a new cover.",
      "B. A chef abandoning traditional recipes in favour of modern ones.",
      "C. A museum closing a gallery because its exhibits were found to be forgeries.",
      "D. A museum describing its medieval garden as \"a reconstruction based on surviving records\" instead of \"an exact replica\"."
    ],
    answer: "D"
  },
  {
    id: 9,
    type: "mcq",
    passageId: "rc3",
    passage: `It has become a commonplace that reading literary fiction makes us better people. The argument runs that novels train us to inhabit other minds, and that this practice carries over into ordinary life, making readers more attentive to the feelings of those around them. A handful of well-publicised experiments seemed to confirm it: participants who read a short literary story performed better on tests of reading emotions from photographs of faces than those who read popular fiction or nothing at all. Attempts to replicate these results, however, have mostly failed, and the correlational evidence — that habitual readers score higher on such tests — is consistent with the possibility that empathetic people are simply drawn to novels.

Defenders of fiction might reasonably feel that the case was badly framed from the start. Reading a story for ten minutes and then identifying expressions in photographs is a strange model of what novels do. The claim worth making is not that a novel delivers a measurable dose of empathy, like a vitamin, but that it offers experiences — of complexity, of moral ambiguity, of lives unlike one's own — whose value does not depend on their producing better behaviour afterwards. To rest the defence of literature on its side effects is to concede that it has no worth in itself, and to make its standing hostage to the next round of experiments. That is a bargain its defenders would do better to refuse.`,
    question: "The correlational evidence mentioned in the passage is inconclusive because:",
    options: [
      "A. habitual readers were not tested on photographs of faces.",
      "B. the tests of reading emotions were poorly designed.",
      "C. it relied on too small a sample of readers.",
      "D. the link could arise because empathetic people choose to read novels, rather than because novels make people empathetic."
    ],
    answer: "D"
  },
  {
    id: 10,
    type: "mcq",
    passageId: "rc3",
    question: "The author's view of the experiments on fiction and empathy is that they:",
    options: [
      "A. relied on a narrow and artificial model of what reading fiction involves.",
      "B. conclusively proved that fiction has no effect on empathy.",
      "C. were well designed but unfairly dismissed by critics.",
      "D. should be repeated with longer novels to settle the question."
    ],
    answer: "A"
  },
  {
    id: 11,
    type: "mcq",
    passageId: "rc3",
    question: "The \"bargain\" that the author advises defenders of literature to refuse is:",
    options: [
      "A. accepting funding for literature in exchange for scientific testing.",
      "B. allowing popular fiction to be taught alongside literary fiction.",
      "C. resting the case for literature on its measurable effects on behaviour.",
      "D. conceding that literature cannot be studied scientifically."
    ],
    answer: "C"
  },
  {
    id: 12,
    type: "mcq",
    passageId: "rc3",
    question: "With which of the following statements would the author most likely agree?",
    options: [
      "A. Reading fiction is valuable only if it improves the reader's conduct.",
      "B. Even if reading fiction were shown to have no effect on empathy, that would not show it to be without value.",
      "C. Popular fiction is as effective as literary fiction in developing empathy.",
      "D. Experiments on reading should be abandoned because they are always inconclusive."
    ],
    answer: "B"
  },
  {
    id: 13,
    type: "mcq",
    passageId: "rc4",
    passage: `Countries rich in oil, gas or minerals might be expected to grow faster than those without such wealth. Over much of the late twentieth century, many did the opposite, a pattern economists labelled the "resource curse". Several mechanisms have been proposed. A boom in resource exports raises the value of the currency, making manufacturing and agriculture less competitive abroad — the so-called Dutch disease. Revenues from resources fluctuate with world prices, encouraging governments to spend lavishly in good years and cut painfully in bad ones. And because such revenues flow to the state without needing to be raised through taxation, rulers may feel less need to win the consent of citizens, weakening the institutions that hold them to account.

The last mechanism has attracted the most attention, but it also complicates the story. If weak institutions are what turn resources into a curse, then resources may be less the cause of poor performance than an amplifier of conditions already present. Norway and Botswana, both heavily dependent on resource revenues, built funds to smooth spending and maintained relatively accountable governments; neither suffered the curse in its usual form. Some economists have gone further, arguing that the statistical pattern itself is partly an artefact: countries whose other industries failed came to depend on resources by default, so that dependence was a symptom of weak growth rather than its cause. On this view the curse is real for some countries, but it is a conditional curse, and the condition is political.`,
    question: "The primary purpose of the passage is to:",
    options: [
      "A. argue that natural resources are always harmful to economic growth.",
      "B. explain how Norway and Botswana managed their resource revenues.",
      "C. outline proposed explanations for the resource curse and argue that its effects depend largely on pre-existing political conditions.",
      "D. show that the Dutch disease is the most important cause of the resource curse."
    ],
    answer: "C"
  },
  {
    id: 14,
    type: "mcq",
    passageId: "rc4",
    question: "Norway and Botswana are mentioned in order to:",
    options: [
      "A. show that dependence on resources need not lead to poor performance when governments remain accountable.",
      "B. prove that the resource curse does not exist.",
      "C. illustrate the effects of the Dutch disease on manufacturing.",
      "D. suggest that small countries are better at managing resources than large ones."
    ],
    answer: "A"
  },
  {
    id: 15,
    type: "mcq",
    passageId: "rc4",
    question: "The argument that the statistical pattern is \"partly an artefact\" suggests that:",
    options: [
      "A. data on resource-rich countries have been deliberately falsified.",
      "B. the causal link may partly run in reverse, with weak growth leading to resource dependence.",
      "C. resource prices are too volatile to be measured accurately.",
      "D. most resource-rich countries in fact grew faster than others."
    ],
    answer: "B"
  },
  {
    id: 16,
    type: "mcq",
    passageId: "rc4",
    question: "Which of the following is NOT presented in the passage as a mechanism behind the resource curse?",
    options: [
      "A. A rise in the value of the currency that makes other exports less competitive.",
      "B. Government spending that swings with fluctuating world prices.",
      "C. Weakened accountability because the state does not depend on taxing its citizens.",
      "D. Environmental damage from extraction that reduces agricultural output."
    ],
    answer: "D"
  },
  {
    id: 17,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. Ironically, it was the very success of the drug that undermined it.\n2. When penicillin became widely available in the 1940s, it was hailed as a cure for almost every bacterial infection.\n3. Within a few years, strains of bacteria that could survive it had begun to spread through hospitals.\n4. Prescribed freely, often for illnesses it could not treat, it gave resistant bacteria every opportunity to flourish.",
    answer: "2143"
  },
  {
    id: 18,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. It is the second kind that, more often than not, produces the actual discovery.\n2. The first is spent gathering data.\n3. The second, by contrast, is spent reconciling those findings with what the team already believed.\n4. A typical research project, one scientist has joked, consumes two kinds of time.",
    answer: "4231"
  },
  {
    id: 19,
    type: "tita",
    question: "The five sentences (labelled 1, 2, 3, 4, 5) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the five numbers as your answer.\n1. These marks, called watermarks, were made by wire designs sewn into the mould.\n2. Historians have learned to read them as a kind of signature of the mill that made the paper.\n3. Paper made by hand was formed by dipping a wire mould into a vat of pulp.\n4. And because moulds wore out and were replaced every year or two, a watermark can often date a sheet to within a few years.\n5. Where the wire pressed into the wet pulp, the paper was left slightly thinner, leaving a faint design visible against the light.",
    answer: "35124"
  },
  {
    id: 20,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nOpen-plan offices were adopted partly on the promise that removing walls would increase face-to-face collaboration. Studies that tracked employees with sensors before and after such moves found the opposite: face-to-face interaction fell substantially, while email and messaging rose. Faced with constant visibility and noise, workers appear to seek privacy by withdrawing into screens and headphones. The architecture designed to bring people together seems instead to have pushed them apart.",
    options: [
      "A. Open-plan offices are cheaper to build but less productive than traditional offices.",
      "B. Email and messaging have replaced face-to-face collaboration in modern workplaces.",
      "C. Open-plan offices reduced face-to-face interaction, as workers seeking privacy turned to digital communication — the reverse of what was intended.",
      "D. Workers prefer private offices because they dislike constant contact with colleagues."
    ],
    answer: "C"
  },
  {
    id: 21,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nHistorians once explained the fall of empires by pointing to decadence, a moral softening that supposedly followed prosperity. The explanation has fallen out of favour, not because empires never declined but because \"decadence\" was rarely defined independently of the decline it was meant to explain: a society was judged decadent because it fell, and said to have fallen because it was decadent. Modern accounts prefer causes that can be identified in advance — fiscal strain, military overextension, climate shocks — even if they lack the moral drama of the older story.",
    options: [
      "A. Empires fall because of fiscal strain, military overextension and climate shocks, not moral decline.",
      "B. Modern historians have proved that decadence played no part in the fall of empires.",
      "C. The older explanation of imperial decline was more dramatic and therefore more popular.",
      "D. Decadence lost favour as an explanation of imperial decline because it was circular, and historians now prefer causes that can be identified independently."
    ],
    answer: "D"
  },
  {
    id: 22,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nRecycling is often promoted as the most important thing individuals can do for the environment. Yet among the familiar \"three Rs\", recycling comes last for a reason: reducing consumption and reusing goods avoid the energy and materials needed to make new products, whereas recycling recovers only part of that cost and itself consumes energy. An emphasis on recycling can even license greater consumption, by easing the guilt attached to disposable goods.",
    options: [
      "A. Recycling consumes more energy than it saves and should be discouraged.",
      "B. Though promoted as paramount, recycling is less beneficial than reducing and reusing, and emphasising it may even encourage consumption.",
      "C. Individuals cannot make a meaningful difference to the environment.",
      "D. Disposable goods should be banned because recycling them is inefficient."
    ],
    answer: "B"
  },
  {
    id: 23,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Octopuses can change both the colour and the texture of their skin in a fraction of a second.\n2. They do so using specialised cells called chromatophores, which expand or contract under the direct control of the nervous system.\n3. Strikingly, octopus skin contains light-sensitive proteins, raising the possibility that it can detect light directly.\n4. Octopuses have three hearts and blue, copper-based blood.\n5. This might help explain how animals thought to be colour-blind can match their surroundings so precisely.",
    answer: "4"
  },
  {
    id: 24,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. The first public lending libraries in many towns were funded by private benefactors.\n2. Public libraries were later brought under the control of local councils and funded through taxation.\n3. Benefactors often attached conditions, such as restrictions on the kinds of books that could be bought.\n4. Some insisted that novels, then thought morally doubtful, be kept off the shelves.\n5. Librarians found ways around such rules, quietly shelving popular fiction under \"history\" or \"travel\".",
    answer: "2"
  }
];

export default catMock4Varc;
